import React from 'react'
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Legend,
  Category,
  StackingColumnSeries,
  Tooltip,
  ILoadedEventArgs,
  ChartTheme,
  IAxisLabelRenderEventArgs,
  Highlight,
  DataLabel,
} from '@syncfusion/ej2-react-charts'
import { Browser } from '@syncfusion/ej2-base'

type StackedColumnChartProps = {
  chartdata: {
    info: any[]
    chartHeight: string | undefined
    chartWidth: string | undefined
    maximumValue: number | undefined
    labelFormat: string | undefined
  }
}

const StackedColumnChart: React.FC<StackedColumnChartProps> = ({
  chartdata,
}) => {
  const onChartLoad = (args: ILoadedEventArgs): void => {
    const chart = document.getElementById('charts') as HTMLElement
    if (chart) {
      chart.setAttribute('title', '')
    }
  }
  const load = (args: ILoadedEventArgs): void => {
    let selectedTheme: string = location.hash.split('/')[1]
    selectedTheme = selectedTheme ? selectedTheme : 'Fluent2'
    args.chart.theme = (
      selectedTheme.charAt(0).toUpperCase() + selectedTheme.slice(1)
    )
      .replace(/-dark/i, 'Dark')
      .replace(/contrast/i, 'Contrast')
      .replace(/-highContrast/i, 'HighContrast') as ChartTheme

    chartdata?.info?.map((x: any) => {
      args.chart.palettes.push(x.barColor)
    })
  }
  const axisLabelRender = (args: IAxisLabelRenderEventArgs): void => {
    args.text = args.text.replace('0000000', '0M').replace('000000', 'M')
  }

  const textRender = (args: any): void => {
    if (args.text === '$0') {
      args.cancel = args.point.y === 0
    }
  }

  const getSeriesCollectionDirective = (data: any): React.ReactElement => {
    return (
      <SeriesCollectionDirective>
        {data?.info?.map((x: any) => {
          return (
            <SeriesDirective
              key={x.name}
              dataSource={x.data}
              xName="x"
              yName="y"
              name={x.name}
              columnWidth={0.6}
              border={{ width: 1, color: x.barBorderColor }}
              type="StackingColumn"
              stackingGroup={x.group ?? ''}
              marker={{
                dataLabel: {
                  name: 'text',
                  visible: true,
                  margin: {
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                  },
                  border: { width: 5, color: 'transparent' },
                  rx: 20,
                  ry: 20,
                  position: x.dataLabel.position,
                  font: { color: x.dataLabel.fontColor },
                },
              }}
              legendShape="Circle"
            />
          )
        })}
      </SeriesCollectionDirective>
    )
  }

  return (
    <div className="control-pane">
      <div className="control-section">
        <ChartComponent
          id="charts"
          style={{
            textAlign: 'center',
            height: `${chartdata.chartHeight ?? 'unset'}`,
            width: `${chartdata.chartWidth ?? 'unset'}`,
          }}
          legendSettings={{
            enableHighlight: true,
          }}
          primaryXAxis={{
            majorGridLines: { width: 0 },
            minorGridLines: { width: 0 },
            majorTickLines: { width: 0 },
            minorTickLines: { width: 0 },
            interval: 1,
            lineStyle: { width: 0 },
            labelIntersectAction: 'Rotate45',
            valueType: 'Category',
          }}
          primaryYAxis={{
            lineStyle: { width: 0 },
            majorTickLines: { width: 0 },
            majorGridLines: { width: 1 },
            minorGridLines: { width: 1 },
            minorTickLines: { width: 0 },
            maximum: chartdata.maximumValue ?? undefined,
            labelFormat: chartdata.labelFormat ?? undefined,
          }}
          width={Browser.isDevice ? '100%' : '75%'}
          chartArea={{ border: { width: 0 } }}
          load={load.bind(this)}
          loaded={onChartLoad.bind(this)}
          tooltip={{ enable: true }}
          axisLabelRender={axisLabelRender.bind(this)}
          textRender={textRender}
        >
          <Inject
            services={[
              StackingColumnSeries,
              Category,
              Legend,
              Tooltip,
              Highlight,
              DataLabel,
            ]}
          />
          {getSeriesCollectionDirective(chartdata)}
        </ChartComponent>
      </div>
    </div>
  )
}

export default StackedColumnChart
